import { useState, useEffect } from 'react';
import { Music, Plus, Trash2, Edit2, Play, Save, Link } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

interface StoryMusic {
  uuid: string;
  name: string;
  description: string;
  category: string;
  music_url: string;
  created_at: string;
}

export default function MusicManager() {
  const [musicList, setMusicList] = useState<StoryMusic[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentMusic, setCurrentMusic] = useState<Partial<StoryMusic>>({});
  
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [audioPreview, setAudioPreview] = useState<string | null>(null);

  useEffect(() => {
    fetchMusic();
  }, []);

  const fetchMusic = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('token');
      const res = await fetch(`${API_URL}/music`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMusicList(data);
      }
    } catch (err) {
      console.error('Failed to fetch music:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentMusic.name || !currentMusic.music_url) {
      alert('Name and Music URL are required');
      return;
    }
    
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/music`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(currentMusic)
      });
      
      if (res.ok) {
        setIsEditing(false);
        fetchMusic();
      } else {
        alert('Failed to save music');
      }
    } catch (error) {
      console.error('Save error', error);
      alert('Error saving music');
    }
  };

  const handleDelete = async (uuid: string) => {
    if (!confirm('Are you sure you want to delete this background music?')) return;
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('token');
      const res = await fetch(`${API_URL}/music/${uuid}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchMusic();
        setSelectedIds(prev => prev.filter(id => id !== uuid));
      }
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Are you sure you want to delete ${selectedIds.length} items?`)) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('token');
      await Promise.all(selectedIds.map(uuid => 
        fetch(`${API_URL}/music/${uuid}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ));
      setSelectedIds([]);
      await fetchMusic();
    } catch (err) {
      console.error('Bulk delete error:', err);
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (music?: StoryMusic) => {
    if (music) {
      setCurrentMusic(music);
    } else {
      setCurrentMusic({
        name: '',
        description: '',
        category: '',
        music_url: ''
      });
    }
    setIsEditing(true);
  };

  return (
    <div className="max-w-6xl mx-auto py-8 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Music className="text-indigo-600" />
            Background Music Manager
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage background music files for VOD Stories.</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="bg-red-50 text-red-600 px-4 py-2 rounded-lg flex items-center hover:bg-red-100 transition-colors border border-red-200 shadow-sm"
            >
              <Trash2 className="w-5 h-5 mr-2" />
              Delete Selected ({selectedIds.length})
            </button>
          )}
          <button 
            onClick={() => openEditor()}
            className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 transition-colors shadow-sm"
          >
            <Plus size={18} />
            Add New Music
          </button>
        </div>
      </div>

      {isEditing && (
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm mb-6">
          <h2 className="text-lg font-semibold mb-4 text-slate-800 flex items-center gap-2">
            <Edit2 size={20} className="text-indigo-600" />
            {currentMusic.uuid ? 'Edit Music Details (New Entry)' : 'Add New Background Music'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Music Name *</label>
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  value={currentMusic.name || ''}
                  onChange={e => setCurrentMusic({...currentMusic, name: e.target.value})}
                  placeholder="e.g. Mysterious Desert Night"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  value={currentMusic.description || ''}
                  onChange={e => setCurrentMusic({...currentMusic, description: e.target.value})}
                  placeholder="A short description..."
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  value={currentMusic.category || ''}
                  onChange={e => setCurrentMusic({...currentMusic, category: e.target.value})}
                  placeholder="e.g. Ambient, Suspense, Action"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Music File URL (MP3/WAV) *</label>
                <input
                  type="url"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  value={currentMusic.music_url || ''}
                  onChange={e => setCurrentMusic({...currentMusic, music_url: e.target.value})}
                  placeholder="https://example.com/music.mp3"
                />
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              onClick={() => setIsEditing(false)}
              className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 font-medium"
            >
              <Save size={18} />
              Save Music
            </button>
          </div>
        </div>
      )}

      {audioPreview && (
        <div className="fixed bottom-4 right-4 bg-slate-900 text-white p-4 rounded-xl shadow-xl z-50 flex items-center gap-4 animate-in slide-in-from-bottom-5">
          <div className="flex-1">
            <p className="text-sm font-medium">Now Playing Preview</p>
            <p className="text-xs text-slate-400 truncate max-w-[200px]">{audioPreview.split('/').pop()}</p>
          </div>
          <audio controls autoPlay src={audioPreview} className="h-8 max-w-[250px]" />
          <button onClick={() => setAudioPreview(null)} className="text-slate-400 hover:text-white">✕</button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500 flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4"></div>
            Loading music library...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4">
                    <input 
                      type="checkbox" 
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      checked={musicList.length > 0 && selectedIds.length === musicList.length}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedIds(musicList.map(m => m.uuid));
                        else setSelectedIds([]);
                      }}
                    />
                  </th>
                  <th className="px-6 py-4">Name</th>
                  <th className="px-6 py-4">Description</th>
                  <th className="px-6 py-4">Category</th>
                  <th className="px-6 py-4">Added</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {musicList.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500 bg-slate-50/50">
                      No music found. Click Add New Music to get started.
                    </td>
                  </tr>
                ) : (
                  musicList.map(music => (
                    <tr key={music.uuid} className="hover:bg-slate-50 transition-colors group">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input 
                          type="checkbox" 
                          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          checked={selectedIds.includes(music.uuid)}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedIds(prev => [...prev, music.uuid]);
                            else setSelectedIds(prev => prev.filter(id => id !== music.uuid));
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-900">
                        <div className="flex items-center gap-3">
                          <button 
                            onClick={() => setAudioPreview(music.music_url)}
                            className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-colors"
                          >
                            <Play size={14} className="ml-0.5" />
                          </button>
                          {music.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600 max-w-xs truncate" title={music.description}>
                        {music.description || '-'}
                      </td>
                      <td className="px-6 py-4">
                        {music.category ? (
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                            {music.category}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 text-slate-600 whitespace-nowrap">
                        {new Date(music.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`${API_URL}/music/${music.uuid}`);
                              alert('API Link copied to clipboard!');
                            }}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Copy API Link"
                          >
                            <Link size={16} />
                          </button>
                          <button 
                            onClick={() => openEditor(music)}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            onClick={() => handleDelete(music.uuid)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
